import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { Button, ButtonGroup, StandardButton } from '@eika/button';
import { ErrorCard, SuccessCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { AttentionLabel } from '@eika/label';
import { ListPanelWrapper } from '@eika/link-panel';
import { Loader } from '@eika/loader';
import { Modal } from '@eika/modal';
import { RichIconTypes } from '@eika/rich-icon';

import { useClick2PayEligibleCards } from '@/react-query/hooks/query/use-click2pay';
import { useClick2PayIsAllowed } from '@/react-query/hooks/useClick2PayIsAllowed';

import useText from '@hooks/use-text';
import CardRow from '@pages/click2pay/CardRow/CardRow';
import Click2PayInfoReview from '@pages/click2pay/onboarding/Click2PayInfoReview/Click2PayInfoReview';

import './Click2Pay.scss';

const Click2PayAdmin = () => {
    const text = useText();
    const cards = useClick2PayEligibleCards();

    const navigate = useNavigate();
    const [openDeletionModal, setOpenDeletionModal] = useState(false);
    const [showInfoContent, setShowInfoContent] = useState(false);
    const click2PayIsAllowed = useClick2PayIsAllowed();

    useEffect(() => {
        if (!click2PayIsAllowed) {
            navigate('/');
        }
    }, [click2PayIsAllowed, navigate]);

    useEffect(() => {
        const handleAnyClick = () => {
            if (showInfoContent) {
                setShowInfoContent(false);
            }
        };
        let timer;
        if (showInfoContent) {
            timer = setTimeout(() => {
                document.addEventListener('click', handleAnyClick);
            }, 100);
        }
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
            document.removeEventListener('click', handleAnyClick);
        };
    }, [showInfoContent]);

    if (cards.isError) {
        return (
            <>
                <ErrorCard
                    title={text.messageErrorUps}
                    icon={RichIconTypes.NETTVERKSBRUDD_HARE}
                    description={text.click2PayGenericError}
                    exception={cards.error ?? undefined}
                />
                <ButtonGroup
                    primaryAction={
                        <StandardButton onClick={() => navigate('/')}>{text.click2PayGoToHome}</StandardButton>
                    }
                />
            </>
        );
    }

    if (cards.isPending) {
        return <Loader />;
    }

    return (
        <>
            <div className="click2pay-header">
                <h1>Click to Pay</h1>
                <p>Her kan du administrere Click to Pay på kortene dine, samt endre kontaktinformasjonen din.</p>
            </div>
            <Button buttonType="link" icon={IconTypes.INFO_20} onClick={() => setShowInfoContent(!showInfoContent)}>
                {text.whatIsClick2Pay}
            </Button>
            <AttentionLabel text={text.click2PayInfoText} hidden={!showInfoContent} />

            <ListPanelWrapper
                hideBackground
                titleContent={
                    <div>
                        <h2>{text.click2PayAdminCardTitle}</h2>
                    </div>
                }
                className="c2p-list-panel"
            >
                <CardRow cards={cards.data} />
            </ListPanelWrapper>
            <h2>Kontaktinfo</h2>
            <p>
                Dette er kontaktinfoen vi deler med Visa. E-posten velger du selv, og brukes som innlogging i Visas
                betalingsløsning i nettbutikkene.
            </p>
            <Click2PayInfoReview />
            <Modal
                open={openDeletionModal}
                setOpen={setOpenDeletionModal}
                onClose={() => navigate('/')}
                onCloseDownSwipe
                shouldCloseOnOverlayClick
                contentShouldStretch
            >
                <SuccessCard title={text.click2payAccountDeleted} centeredWithMargin>
                    <Button onClick={() => navigate('/')}>{text.click2PayGoToHome}</Button>
                </SuccessCard>
            </Modal>
        </>
    );
};

export default Click2PayAdmin;
